/**
 * Shopify Storefront API Client
 * For public data like products, collections, metaobjects
 */

class StorefrontClient {
    constructor() {
        this.shopDomain = window.Shopify?.shop || window.location.hostname;
        this.storefrontAccessToken = '4894686455994e9f30ec76fc0246ad24';
        this.apiVersion = '2024-01';
    }

    /**
     * Make GraphQL request to Storefront API
     */
    async graphql(query, variables = {}) {
        const endpoint = `https://${this.shopDomain}/api/${this.apiVersion}/graphql.json`;

        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken,
            },
            body: JSON.stringify({ query, variables }),
        });

        if (!response.ok) {
            throw new Error(`Storefront API request failed: ${response.status}`);
        }

        const result = await response.json();

        if (result.errors) {
            console.error('Storefront API errors:', result.errors);
            throw new Error(result.errors[0]?.message || 'GraphQL error');
        }

        return result.data;
    }
}

// Export singleton instance
export const storefrontClient = new StorefrontClient();

// Common queries for videos/metaobjects
export const STOREFRONT_QUERIES = {
    // Get videos from metaobjects
    GET_VIDEOS: `
        query getVideos($first: Int!) {
            metaobjects(type: "video", first: $first) {
                edges {
                    node {
                        id
                        handle
                        fields {
                            key
                            value
                            reference {
                                __typename
                                ... on MediaImage {
                                    id
                                    image {
                                        url
                                        altText
                                        width
                                        height
                                    }
                                }
                                ... on GenericFile {
                                    url
                                }
                            }
                        }
                    }
                }
            }
        }
    `,
};

/**
 * Transform metaobject fields array into a simple object
 */
export function transformMetaobjectFields(fields) {
    return fields.reduce((acc, field) => {
        acc[field.key] = field.value;
        return acc;
    }, {});
}

/**
 * Fetch media URL by GID from Shopify
 */
async function fetchMediaByGid(gid) {
    try {
        const query = `
            query getMedia($id: ID!) {
                node(id: $id) {
                    ... on MediaImage {
                        image {
                            url
                        }
                    }
                }
            }
        `;
        
        const data = await storefrontClient.graphql(query, { id: gid });
        return data?.node?.image?.url || null;
    } catch (error) {
        console.error('Failed to fetch media by GID:', error);
        return null;
    }
}

/**
 * Transform video metaobjects into the format expected by the app
 * Filters to only include videos with "active" status, sorts by date, and limits to 30 videos
 */
export async function transformVideos(metaobjects) {
    if (!metaobjects?.edges) return [];
    
    // First pass: collect all videos and GIDs that need to be resolved
    const videosWithGids = [];
    
    const allVideos = await Promise.all(metaobjects.edges
        .map(async ({ node }) => {
            const fields = {};
            let previewImageUrl = null;
            let previewImageGid = null;
            
            // Log all fields for debugging the most recent videos
            const isRecentVideo = node.handle.includes('12-1') || node.handle.includes('12-29') || node.handle.includes('12-28');
            if (isRecentVideo) {
                console.log(`🔍 All fields for ${node.handle}:`, node.fields);
                console.log(`🔍 Field keys for ${node.handle}:`, node.fields.map(f => `${f.key} (${f.value ? 'has value' : 'no value'}, ${f.reference ? 'has reference' : 'no reference'})`));
            }
            
            // Process fields and extract preview image
            node.fields.forEach(field => {
                if (field.key === 'image') {
                    // Use the same pattern as badges: check field.reference?.image first
                    if (field.reference?.image) {
                        previewImageUrl = field.reference.image.url;
                    } 
                    // Check if it's stored as a plain URL string in value
                    else if (field.value && typeof field.value === 'string' && field.value.startsWith('http')) {
                        previewImageUrl = field.value;
                    }
                    // If value is a GID, store it for later resolution
                    else if (field.value && typeof field.value === 'string' && field.value.startsWith('gid://')) {
                        previewImageGid = field.value;
                    }
                } else {
                    fields[field.key] = field.value;
                }
            });
            
            // If we have a GID but no URL, fetch it
            if (previewImageGid && !previewImageUrl) {
                previewImageUrl = await fetchMediaByGid(previewImageGid);
            }
            
            const vimeoId = fields.vimeo_id;
            
            // Parse tags - handle both JSON array string and comma-separated string
            let tags = [];
            if (fields.tags) {
                try {
                    // Try parsing as JSON first
                    tags = JSON.parse(fields.tags);
                } catch (e) {
                    // If not JSON, treat as comma-separated string
                    tags = fields.tags.split(',').map(t => t.trim());
                }
            }
            
            return {
                id: fields.video_id || node.handle,
                title: fields.title || 'Untitled Workout',
                vimeoId: vimeoId,
                embed: vimeoId, // VideoCard expects this field
                vimeoLink: fields.vimeo_link || `https://vimeo.com/${vimeoId}`,
                previewImageUrl: previewImageUrl, // Add preview image URL
                tags: tags,
                publish_date: fields.publish_date,
                publish_time: fields.publish_time,
                status: fields.status || 'active', // Default to active if not specified
                handle: node.handle,
            };
        })
    );
    
    // Filter to only include videos with "active" status
    const activeVideos = allVideos.filter(video => video.status?.toLowerCase() === 'active');
    
    // Sort by publish_date (newest first)
    const sortedVideos = [...activeVideos].sort((a, b) => {
        const dateA = new Date(a.publish_date + 'T00:00:00');
        const dateB = new Date(b.publish_date + 'T00:00:00');
        return dateB - dateA; // Newest first
    });
    
    // Return all active videos (no limit here - let the month filter handle limiting)
    return sortedVideos;
}

/**
 * Extract unique categories from videos
 */
export function extractCategories(videos) {
    const categoriesMap = new Map();
    categoriesMap.set('all', 'All'); // Always include 'All' first
    
    videos.forEach(video => {
        if (video.tags && Array.isArray(video.tags)) {
            video.tags.forEach(tag => {
                if (tag) {
                    // Normalize the tag: lowercase, replace underscores with spaces, trim
                    const normalizedTag = tag.toLowerCase().replace(/_/g, ' ').trim();
                    // Store with normalized key to avoid duplicates
                    if (!categoriesMap.has(normalizedTag)) {
                        // Properly capitalize the tag
                        const formattedTag = normalizedTag
                            .split(' ')
                            .map(word => {
                                // Special case for HIIT
                                if (word.toLowerCase() === 'hiit') {
                                    return 'HIIT';
                                }
                                // Capitalize first letter, keep rest lowercase
                                return word.charAt(0).toUpperCase() + word.slice(1);
                            })
                            .join(' ');
                        categoriesMap.set(normalizedTag, formattedTag);
                    }
                }
            });
        }
    });
    
    // Return the properly formatted tag values
    return Array.from(categoriesMap.values());
}
